'use client';

import React from 'react';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Tooltip, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';
import type { StreakBadge, StreakStatus } from '@/types/streak.types';

// Same mapping used by the header/portfolio streak widgets — a "rank up"
// progression from bronze through the legendary 1000-day ember tone.
const BADGE_COLORS: Record<number, string> = {
  7: '#cd7f32',
  30: colors.neutral[400],
  100: '#f5b301',
  180: '#10b981',
  365: colors.info.main,
  500: '#8b5cf6',
  1000: '#e8b046',
};

interface StreakBadgesShowcaseProps {
  data: StreakStatus;
}

export const StreakBadgesShowcase: React.FC<StreakBadgesShowcaseProps> = ({ data }) => {
  const { t } = useT();

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        {t('streaks.badgesTitle', { defaultValue: 'Badges' })}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 620 }}>
        {t('streaks.badgesSubtitle', {
          defaultValue:
            'Milestones based on your longest-ever streak. Once unlocked, a badge is yours for good.',
        })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
          gap: 1.5,
        }}
      >
        {data.badges.map((badge) => (
          <BadgeCard key={badge.threshold} badge={badge} currentStreak={data.consecutiveDays} />
        ))}
      </Box>
    </Box>
  );
};

const BadgeCard: React.FC<{ badge: StreakBadge; currentStreak: number }> = ({
  badge,
  currentStreak,
}) => {
  const { t } = useT();
  const color = BADGE_COLORS[badge.threshold] || '#e8b046';
  const remaining = Math.max(0, badge.threshold - currentStreak);

  return (
    <Tooltip
      title={
        badge.achieved
          ? t('streaks.badgeAchievedTooltip', {
              defaultValue: `${badge.threshold}-day streak — unlocked`,
            })
          : currentStreak > 0
            ? t('streaks.badgeRemainingTooltip', {
                defaultValue: `${remaining} more day${remaining === 1 ? '' : 's'} to unlock`,
              })
            : t('streaks.badgeLockedTooltip', {
                defaultValue: `Reach a ${badge.threshold}-day streak to unlock`,
              })
      }
      arrow
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          py: 2,
          px: 1,
          borderRadius: 3,
          border: '1px solid',
          borderColor: badge.achieved ? `${color}55` : 'divider',
          bgcolor: badge.achieved ? `${color}14` : 'transparent',
          opacity: badge.achieved ? 1 : 0.65,
          transition: 'transform 0.15s ease',
          '&:hover': { transform: 'translateY(-2px)' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: badge.achieved ? `${color}22` : 'action.hover',
          }}
        >
          {badge.achieved ? (
            <LocalFireDepartmentIcon sx={{ fontSize: 22, color }} />
          ) : (
            <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '0.85rem',
            fontWeight: 800,
            color: badge.achieved ? color : 'text.secondary',
          }}
        >
          {badge.threshold}d
        </Typography>
      </Box>
    </Tooltip>
  );
};
